<template>
  <div class="flex flex-col max-md:mt-8">
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-semibold text-gray-900">Edit Layout Settings</h3>
      <div class="flex gap-2">
        <BaseButton
          type="button"
          class="text-black border p-2 rounded-lg border-gray-300 font-inter text-sm font-medium"
          @click="$emit('cancel')"
        >
          Close
        </BaseButton>
        <BaseButton
          type="button"
          class="bg-primary-purple text-white p-2 rounded-lg font-inter text-sm font-medium"
          @click="$emit('update', layout)"
          :disabled="updateDirectoryLoading"
        >
          {{ updateDirectoryLoading ? "Updating..." : "Update" }}
        </BaseButton>
      </div>
    </div>

    <!-- Layout Name and Type Fields -->
    <div class="flex flex-col gap-4 mb-6 text-left">
      <div class="flex flex-col">
        <label class="text-sm font-medium text-gray-700 mb-2">
          Layout Name
        </label>
        <input
          :value="layout.layoutName || ''"
          @input="(event) => $emit('updateLayoutName', layout.id, (event.target as HTMLInputElement).value)"
          placeholder="Enter layout name"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-purple focus:border-transparent"
        />
      </div>
      <div class="flex flex-col">
        <label class="text-sm font-medium text-gray-700 mb-2">
          Layout Type
        </label>
        <select
          :value="layout.layoutType || 'grid'"
          @change="(event) => $emit('updateLayoutType', layout.id, (event.target as HTMLSelectElement).value)"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-purple focus:border-transparent"
        >
          <option value="grid">Grid</option>
          <option value="single">Single</option>
          <option value="row">Row</option>
        </select>
      </div>
    </div>

    <div class="flex flex-col">
      <TemplateSelector
        :initialSelected="layout.style"
        @templateSelected="
          (template: string) => $emit('updateLayoutStyle', layout.id, template)
        "
      />
    </div>
    <div class="flex flex-col">
      <CardStyling
        :fontFamily="layout.directoryCosmetic.fontFamily"
        :fontSize="layout.directoryCosmetic.fontSize"
        :buttonTextColor="layout.directoryCosmetic.textColor"
        :boldness="layout.directoryCosmetic.bold"
        :buttonBackgroundColor="layout.directoryCosmetic.backgroundColor"
        @fontFamilyChanged="
          (font: string) => $emit('updateLayoutCosmetic', layout.id, 'fontFamily', font)
        "
        @boldnessChanged="
          (bold: string) => $emit('updateLayoutCosmetic', layout.id, 'bold', bold)
        "
        @buttonTextColorChanged="
          (color: string) => $emit('updateLayoutCosmetic', layout.id, 'textColor', color)
        "
        @buttonBackgroundColorChanged="
          (color: string) => $emit('updateLayoutCosmetic', layout.id, 'backgroundColor', color)
        "
        @fontSizeChanged="
          (size: string) => $emit('updateLayoutCosmetic', layout.id, 'fontSize', size)
        "
      />
    </div>
    <div class="flex flex-col">
      <DirectoryHeaderDesign
        :isVisible="layout.directoryHeaderCosmetic.isVisible"
        :fontFamily="layout.directoryHeaderCosmetic.fontFamily"
        :headerFontSize="layout.directoryHeaderCosmetic.headerFontSize"
        :paragraphFontSize="layout.directoryHeaderCosmetic.paragraphFontSize"
        :headerTextColor="layout.directoryHeaderCosmetic.textColor"
        :boldness="layout.directoryHeaderCosmetic.bold"
        :backgroundColor="layout.directoryHeaderCosmetic.backgroundColor"
        :headlineText="layout.directoryHeaderCosmetic.headerText"
        :paragraphText="layout.directoryHeaderCosmetic.paragraphText"
        @paragraphTextChanged="
          (text: string) => $emit('updateLayoutHeader', layout.id, 'paragraphText', text)
        "
        @fontFamilyChanged="
          (font: string) => $emit('updateLayoutHeader', layout.id, 'fontFamily', font)
        "
        @boldnessChanged="
          (bold: string) => $emit('updateLayoutHeader', layout.id, 'bold', bold)
        "
        @textColorChanged="
          (color: string) => $emit('updateLayoutHeader', layout.id, 'textColor', color)
        "
        @backgroundColorChanged="
          (color: string) => $emit('updateLayoutHeader', layout.id, 'backgroundColor', color)
        "
        @headerFontSizeChanged="
          (size: string) => $emit('updateLayoutHeader', layout.id, 'headerFontSize', size)
        "
        @paragraphFontSizeChanged="
          (size: string) => $emit('updateLayoutHeader', layout.id, 'paragraphFontSize', size)
        "
        @headlineTextChanged="
          (text: string) => $emit('updateLayoutHeader', layout.id, 'headerText', text)
        "
        @toggleVisibilityHeader="
          (visible: boolean) => $emit('updateLayoutHeader', layout.id, 'isVisible', visible)
        "
      />
    </div>
    <ExpertSelector
      v-if="layout.layoutType === 'single' || layout.layoutType === 'row'"
      :approvedExperts="getAvailableExperts(layout)"
      :selectedExpertIds="layout.selectedExpertIds || []"
      :layoutType="layout.layoutType"
      :selectRandomExperts="layout.selectRandomExperts || false"
      :layoutId="layout.id"
      :fieldErrors="fieldErrors"
      @expertChanged="
        (ids: string[], fieldIndex?: number) =>
          $emit('updateLayoutSelectedExperts', layout.id, ids, fieldIndex)
      "
      @randomModeChanged="
        (isRandom: boolean) => $emit('updateLayoutRandomExperts', layout.id, isRandom)
      "
    />
    <SearchFiltersToggle
      v-if="layout.layoutType === 'grid'"
      :showSearchFilters="layout.showSearchFilters"
      @searchFiltersChanged="
        (enabled: boolean) => $emit('updateLayoutSearchFilters', layout.id, enabled)
      "
    />
    <WebsiteUrl
      :websiteUrl="layout.websiteUrl"
      :companyWebsite="userStore.companyWebsite || ''"
      @websiteUrlChanged="(url: string) => $emit('updateLayoutWebsite', layout.id, url)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import CardStyling from "@/components/expert-program/directory/CardStyling.vue";
import TemplateSelector from "@/components/expert-program/directory/TemplateSelector.vue";
import DirectoryHeaderDesign from "@/components/expert-program/DirectoryHeaderDesign.vue";
import ExpertSelector from "@/components/expert-program/directory/ExpertSelector.vue";
import SearchFiltersToggle from "@/components/expert-program/directory/SearchFiltersToggle.vue";
import WebsiteUrl from "@/components/expert-program/directory/WebsiteUrl.vue";
import { DirectoryLayout, ApprovedExpert } from "@/types/dashboard-types";

export default defineComponent({
  name: "DirectoryControls",
  components: {
    BaseButton,
    CardStyling,
    TemplateSelector,
    DirectoryHeaderDesign,
    ExpertSelector,
    SearchFiltersToggle,
    WebsiteUrl,
  },
  props: {
    layout: {
      type: Object as () => DirectoryLayout,
      required: true,
    },
    approvedExperts: {
      type: Array as () => ApprovedExpert[],
      required: true,
    },
    fieldErrors: {
      type: Object as () => Record<string, string>,
      required: true,
    },
    updateDirectoryLoading: {
      type: Boolean,
      default: false,
    },
    userStore: {
      type: Object,
      required: true,
    },
  },
  emits: [
    "update",
    "cancel",
    "updateLayoutName",
    "updateLayoutType",
    "updateLayoutStyle",
    "updateLayoutCosmetic",
    "updateLayoutHeader",
    "updateLayoutWebsite",
    "updateLayoutSearchFilters",
    "updateLayoutSelectedExperts",
    "updateLayoutRandomExperts",
  ],
  setup(props) {
    // Get available experts for selection
    function getAvailableExperts(layout: DirectoryLayout): ApprovedExpert[] {
      // Return all approved experts without filtering
      return props.approvedExperts;
    }

    return {
      getAvailableExperts,
    };
  },
});
</script>
